import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { CursorState } from '@editor/config/constants/cursor'
import { Identifiable, PointArray } from '@editor/interfaces/attribute'
import { PolylineAnchorProps } from '@editor/interfaces/canvas'
import { SubtoolVertex } from '@editor/interfaces/tool'
import {
  AnchorStates,
  Cursors,
  DefaultStyles,
  NoActionStyles,
  VertexSubtoolStyles,
} from '@editor/pages/projects/editor/tools/Polyline/constants'
import { useAppDispatch } from '@editor/store/app'
import { useToolUtilSelector, useVertexSelector } from '@editor/store/selectors'
import { setCurrentAnchorPosition, setCursor, setEditingElementId, setIsDragging } from '@editor/store/tool'
import { ThreeEvent } from '@react-three/fiber'
import { merge } from 'lodash'

import CircleAnchor from '../../EditorCircleAnchor'

/**
 * The additonal props here are strictly for Polyline to configure anchor behavior, not for useMainCanvas.
 */
interface AnchorProps extends Omit<PolylineAnchorProps, 'id' | 'point' | 'onUpdate'>, Identifiable {
  point: PointArray
  parentId: string
  anchorIndex: number

  /**
   * When anchor is supposed to be interactable (drag, click, etc) but for whatever reason
   * no action is possible (e.g. no subtool selected, or the action is not allowed).
   * This flag will indicate that no action is possible on this anchor.
   */
  isNoAction?: boolean

  /**
   * Whether the anchor is draggable or not.
   */
  isDraggable?: boolean

  /**
   * Whether the parent is being hovered or selected.
   * This is used to adjust functionality or styles based on parent's state.
   */
  isParentHoverSelected: boolean

  /**
   * Highlights the anchor when true.
   * Should not be used for the anchor's own hover state as that is handled internally.
   * Used it for when anchor needs to be highlighted due to external factors.
   */
  isHighlighted?: boolean

  onComplete?: () => void
  onUpdate?: (anchorIndex: number, point: PointArray) => void
}

const Anchor = (props: AnchorProps) => {
  const {
    parentId,
    id,
    point,
    visible = true,
    opacity,

    color,
    outlineColor,
    icon,
    iconColor,
    outlineOpacity,
    iconScale,
    scale,
    hoverScale,

    anchorIndex,
    showOnHover,
    isInteractable,
    isDraggable = true,
    isNoAction,
    isParentHoverSelected,
    isHighlighted,

    onEnter,
    onLeave,
    onDown,
    onUp,
    onUpdate,
    onUpdated,
    onComplete,
  } = props

  // Store
  const dispatch = useAppDispatch()
  const currentAnchorPosition = useToolUtilSelector((state) => state.currentAnchorPosition)
  const currentVertexSubtool = useVertexSelector((state) => state.currentVertexSubtool)
  const availableVertexSubtools = useVertexSelector((state) => state.availableVertexSubtools)

  // States
  const [isHovered, setIsHovered] = useState(false)

  // Refs
  const isEditing = useRef(false)

  /**
   * Get opacity based on visibility and hover state.
   *
   * @param value - The base opacity value.
   * @returns The calculated opacity.
   */
  const getOpacity = useCallback(
    (value?: number) => {
      if (showOnHover && !isParentHoverSelected && !isHovered) return 0
      if (isHovered || isHighlighted) return 1
      return visible || isParentHoverSelected ? value : 0
    },
    [visible, isParentHoverSelected, showOnHover, isHovered, isHighlighted],
  )

  /**
   * Set styles based on current vertex subtoolbar.
   * In terms of priority where former will be overridden by latter:
   * - default styles
   * - subtool base styles
   * - state-based subtool styles (e.g. no action, highlighted, etc)
   * - specific props
   * - opacity state
   */
  const newAnchorProps = useMemo((): Partial<AnchorProps> => {
    const priorityStyles: Partial<AnchorProps> = {
      color,
      outlineColor,
      icon,
      iconColor,
      outlineOpacity: getOpacity(outlineOpacity),
      iconScale,
      scale,
      hoverScale,
    }

    if (!availableVertexSubtools.length || currentVertexSubtool === SubtoolVertex.None || !isInteractable)
      return priorityStyles

    // Apply subtool styles
    const subtoolStyles = {
      ...VertexSubtoolStyles[currentVertexSubtool]?.[AnchorStates.Base],
      ...(isHighlighted ? VertexSubtoolStyles[currentVertexSubtool]?.[AnchorStates.Highlighted] : {}),
    }

    // If no action is possible, use no action styles
    const actionStyles = isNoAction ? NoActionStyles : subtoolStyles

    // Merge all styles together
    const merged = merge({}, DefaultStyles, actionStyles, priorityStyles)

    return merged
  }, [
    color,
    outlineColor,
    icon,
    iconColor,
    outlineOpacity,
    iconScale,
    scale,
    hoverScale,
    currentVertexSubtool,
    availableVertexSubtools.length,
    isInteractable,
    isNoAction,
    isHighlighted,
    getOpacity,
  ])

  /**
   * Handle mouse enter event on the anchor.
   *
   * @param ev - The mouse event.
   */
  const handleMouseEnter = (ev: ThreeEvent<PointerEvent>) => {
    ev.stopPropagation()
    dispatch(setCursor(Cursors[currentVertexSubtool] ?? CursorState.GRAB))
    setIsHovered(true)
    onEnter?.(anchorIndex, ev)
  }

  /**
   * Handle mouse leave event on the anchor.
   *
   * @param ev - The mouse event.
   */
  const handleMouseLeave = (ev: ThreeEvent<PointerEvent>) => {
    ev.stopPropagation()
    dispatch(setCursor(CursorState.NONE))
    setIsHovered(false)
    onLeave?.(anchorIndex, ev)
  }

  /**
   * Handle mouse down event on the anchor.
   *
   * @param ev - The mouse event.
   */
  const handleMouseDown = (ev: ThreeEvent<PointerEvent>) => {
    ev.stopPropagation()
    onDown?.(anchorIndex, ev)

    if (!onUpdated || !isDraggable) return
    dispatch(setCurrentAnchorPosition(undefined))
    dispatch(setEditingElementId(parentId))
    dispatch(setIsDragging(true))
    isEditing.current = true
  }

  /**
   * Handle mouse up event on the anchor.
   * @param ev - The mouse event.
   */
  const handleMouseUp = (ev: ThreeEvent<PointerEvent>) => {
    ev.stopPropagation()
    if (!onUpdated) return
    onUp?.(anchorIndex, ev)
  }

  /**
   * When current anchor position changes, and is in editing mode, call onUpdate.
   */
  useEffect(() => {
    if (!currentAnchorPosition || !isEditing.current || !onUpdated) return
    onUpdate?.(anchorIndex, currentAnchorPosition)
  }, [currentAnchorPosition, isEditing, anchorIndex, onUpdate, onUpdated])

  /**
   * Handle mouse up event on window.
   * This is to capture events outside the anchor and canvas
   * if user's mouse goes out of bounds while dragging.
   */
  useEffect(() => {
    const handleUp = (ev: MouseEvent | TouchEvent) => {
      if (!isEditing.current) return

      ev.stopPropagation()

      onComplete?.()
      isEditing.current = false
      dispatch(setEditingElementId(undefined))
      dispatch(setIsDragging(false))
      setIsHovered(false)
    }

    window.addEventListener('mouseup', handleUp)
    window.addEventListener('touchend', handleUp)
    return () => {
      window.removeEventListener('mouseup', handleUp)
      window.removeEventListener('touchend', handleUp)
    }
  }, [id, parentId, isEditing, onComplete, dispatch])

  return (
    <CircleAnchor
      {...props}
      {...newAnchorProps}
      id={id}
      point={point}
      opacity={getOpacity(opacity)}
      onEnter={onUpdated ? handleMouseEnter : undefined}
      onLeave={onUpdated ? handleMouseLeave : undefined}
      onUp={handleMouseUp}
      onDown={handleMouseDown}
      renderOrder={999}
      touchAreaMultiplier={2}
      visible={visible}
    />
  )
}

export default Anchor
